import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Newspaper } from 'lucide-react';
import IconButton from '../ui/IconButton';
import Tooltip from '../ui/Tooltip';
import { cx } from '../ui/classNames';
import NewsPopover from './NewsPopover';
import MovePill from './MovePill';
import DeepDiveDialog from './DeepDiveDialog';
import LlmSettingsDialog from './LlmSettingsDialog';
import {
    hasStoredCredentials,
    loadLlmCredentials,
} from '../../services/llmCredentials';
import { DEEP_DIVE_ENABLED } from '../../config/deepDiveFeatureFlag';
import { MSI_FONT_FAMILY } from '../../styles/msiStyleTokens';
import {
    newsFetchMessage,
    DEFAULT_RECENCY_WINDOW_HOURS,
    NEW_NEWS_RECENCY_MS as SHARED_NEW_NEWS_RECENCY_MS,
    selectRecentArticles as sharedSelectRecentArticles,
    formatRecencyDotTooltip as sharedFormatRecencyDotTooltip,
} from './NewsCell.recencyHelpers';

/**
 * Re-exported for the callers and tests that already import the flag from
 * here. The value is a build-time setting driven by `VITE_DEEP_DIVE_ENABLED`
 * (Trevor, PR #504 2026-09-08) — see `src/config/deepDiveFeatureFlag.js`.
 * The backend `DEEP_DIVE_ENABLED` env var stays the enforcement boundary;
 * this only decides whether the UI offers the affordance at all.
 */
export { DEEP_DIVE_ENABLED };

/**
 * Legacy recency-window constants (W7) — re-exported from the shared helper
 * module so existing tests importing them from `NewsCell` keep working. W8
 * (2026-08-04) makes the window a per-user preference threaded in via the
 * `recencyWindowHours` prop; the default matches the pre-W8 hardcode.
 */
export { DEFAULT_RECENCY_WINDOW_HOURS };
export const NEW_NEWS_RECENCY_MS = SHARED_NEW_NEWS_RECENCY_MS;

/**
 * Newspaper-icon size (px). W7 shipped at 16 px; W7.2 bumps to 24 px so the
 * icon has visual room to anchor the pill (top-right) + recency dot (bottom-
 * right) as badge overlays without crowding — matches Trevor's 2026-08-03
 * mockup.
 */
export const NEWS_ICON_FONT_SIZE = 24;

// The button's icon colour: secondary while news is being checked, the text
// colour with headlines, MUI's disabled text without; the theme's ink on
// hover, as the icon button's own hover does.
const BUTTON_TONE_CLASS_NAMES = Object.freeze({
    pending: '!text-content-secondary',
    news: '!text-content-primary',
    none: '!text-black/[0.38] dark:!text-white/50',
});
// The badge overlays may overhang the button; it keeps the browser's control
// font as MUI's buttons do, which the move pill's figures are set in.
const BUTTON_CLASS_NAME = 'relative !overflow-visible ![font-family:revert] hover:!text-ui-ink '
    + 'dark:hover:!text-ui-ink';
// The overlays' places on the icon (W7.2): the move pill or its placeholder
// top-right, the recency dot bottom-right.
const PILL_SLOT_STYLE = Object.freeze({ position: 'absolute', top: -4, right: -10 });
// The placeholder is in the app's type (not the button's control font).
const PLACEHOLDER_STYLE = Object.freeze({ position: 'absolute', top: -2, right: -6, fontFamily: MSI_FONT_FAMILY });
const DOT_STYLE = Object.freeze({ position: 'absolute', bottom: 0, right: -2 });
// The placeholder: small, quiet (MUI's disabled text), not a click target of
// its own.
const PLACEHOLDER_CLASS_NAME = 'pointer-events-none z-[2] text-[0.65rem] font-medium leading-none tracking-normal '
    + 'text-black/[0.38] dark:text-white/50';
// The recency dot: MUI's error red (a third of it once read), ringed in the
// surface colour so it stands off the icon.
const DOT_CLASS_NAME = 'z-[1] inline-block h-2 w-2 rounded-full shadow-[0_0_0_2px_rgb(var(--c-surface)/0.9)]';
const DOT_VARIANT_CLASS_NAMES = Object.freeze({
    bright: 'bg-[#d32f2f] dark:bg-[#f44336]',
    dull: 'bg-[#d32f2f]/[0.35] dark:bg-[#f44336]/[0.35]',
});

// Re-export the shared helpers so consumers that already import from NewsCell
// keep working. Implementation lives in NewsCell.recencyHelpers so NewsPopover
// can consume it without a NewsCell ⇄ NewsPopover circular import.
export const selectRecentArticles = sharedSelectRecentArticles;
export const formatRecencyDotTooltip = sharedFormatRecencyDotTooltip;

/**
 * Compact per-row watchlist news affordance — one clickable widget composed of
 * the newspaper icon plus two badge overlays.
 *
 * W7.2 (2026-08-03) recomposition per Trevor's mockup: the news icon, the
 * move pill, and the recency dot are one clickable widget. The IconButton
 * itself is the click target and popover anchor; the pill and dot render as
 * absolutely-positioned overlays on the icon so any click within the widget
 * bounds opens the popover.
 *
 * W8 (2026-08-04) three-state recency dot per Trevor's 2026-07-30 Slack:
 *   - Bright red (MUI's error red) — there are articles inside the recency
 *     window AND at least one is unread.
 *   - Dull red (that red at 35%) — there are articles inside the recency
 *     window and every one of them has already been read (opened the popover
 *     once).
 *   - Hidden — no articles are inside the recency window.
 * The window itself is user-configurable via the `recencyWindowHours` prop
 * (defaults to 24 hr for callers that haven't threaded through the pref yet).
 * `readArticleIds` is the Set from `useWatchlistNews`; passing an empty set is
 * the "everyone unread" case (dot always bright when a recent article exists).
 *
 * Renders nothing when `item` is missing — the news load failing must never
 * break watchlist rendering (Worker 04 spec: "News load failure must degrade
 * silently — must never break watchlist rendering").
 */
export default function NewsCell({
    item,
    moverThresholdPct = null,
    lastUpdatedAt = null,
    deepDiveEnabled = DEEP_DIVE_ENABLED,
    recencyWindowHours = DEFAULT_RECENCY_WINDOW_HOURS,
    readArticleIds = null,
    onArticlesRead = null,
    deepDiveFocusAreas = null,
    // Two-stage news loading (Fix 2, 2026-08-12): the parent owns the
    // per-security detail cache. On popover open we ask it to load the full
    // articles for this security; while that's in flight the popover renders
    // a skeleton, then swaps in the real headlines on arrival.
    onLoadNewsDetails = null,
    securityArticles = null,
    securityArticlesLoading = false,
    securityArticlesError = null,
}) {
    const [anchorEl, setAnchorEl] = useState(null);
    // W6 (2026-08-06) — Layer 2 Deep Dive lifecycle. Owned by the cell so the
    // dialogs survive the popover closing (Popover onClose fires on any
    // backdrop click, including the click that opens the Deep Dive dialog).
    const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
    // Why the settings dialog was opened, when it was not the user's own
    // choice. Cleared on every deliberate open so a stale 401 notice from an
    // earlier attempt cannot linger.
    const [settingsReason, setSettingsReason] = useState(null);
    const [deepDiveOpen, setDeepDiveOpen] = useState(false);
    const [deepDiveCredentials, setDeepDiveCredentials] = useState(null);

    const handleOpen = useCallback((event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        // Kick off the detail fetch as soon as the popover opens. The parent
        // hook dedups concurrent calls and caches results for ~5 min so
        // reopening the same popover is a no-op.
        const cogniveId = item?.cognive_id;
        if (cogniveId && typeof onLoadNewsDetails === 'function') {
            void onLoadNewsDetails(cogniveId);
        }
    }, [item, onLoadNewsDetails]);
    const handleClose = useCallback(() => setAnchorEl(null), []);

    // Called when the popover's Deep Dive button is clicked.
    // If credentials are on file: close the popover, load them, open the
    // DeepDiveDialog. If none: open the settings dialog first; on-save the
    // settings-dialog callback re-fires this flow.
    const handleDeepDiveRequest = useCallback(() => {
        setAnchorEl(null);
        setSettingsReason(null);
        if (!hasStoredCredentials()) {
            setSettingsDialogOpen(true);
            return;
        }
        const stored = loadLlmCredentials();
        if (!stored) {
            // Race — credentials cleared between hasStoredCredentials() and
            // loadLlmCredentials(). Fall through to settings.
            setSettingsDialogOpen(true);
            return;
        }
        setDeepDiveCredentials(stored);
        setDeepDiveOpen(true);
    }, []);

    const handleSettingsClose = useCallback(() => {
        setSettingsDialogOpen(false);
        setSettingsReason(null);
    }, []);

    const handleSettingsSaved = useCallback(() => {
        // After a fresh save (or key rotation triggered by a 401), pick the
        // updated credentials up and continue into the deep dive.
        setSettingsDialogOpen(false);
        setSettingsReason(null);
        const stored = loadLlmCredentials();
        if (!stored) return;
        setDeepDiveCredentials(stored);
        setDeepDiveOpen(true);
    }, []);

    const handleDeepDiveClose = useCallback(() => {
        setDeepDiveOpen(false);
    }, []);

    const handleDeepDiveRequireSettings = useCallback((reason = null) => {
        // Bounce the user to the settings surface so they can rotate the key,
        // and carry the reason across with them.
        //
        // Before 2026-09-15 this relied on the deep-dive dialog's snackbar to
        // explain what happened. That does not work: closing the dialog takes
        // its error state with it, and the user arrives at a bare key prompt
        // with no idea why. Found during the W9 error-state smoke, where a
        // deliberately invalid key produced a key prompt and an error that
        // vanished before it could be read.
        setDeepDiveOpen(false);
        setSettingsReason(typeof reason === 'string' && reason ? reason : null);
        setSettingsDialogOpen(true);
    }, []);

    // Metadata `recent_articles` is a lightweight [{article_id, published_at}]
    // list — enough for count / dot / mark-read logic. Full article bodies
    // live in `securityArticles` (from the parent's detail-cache hook) and
    // only arrive after the popover opens.
    const articleStubs = useMemo(
        () => (Array.isArray(item?.recent_articles) ? item.recent_articles : []),
        [item],
    );
    const recentArticles = useMemo(
        () => selectRecentArticles(articleStubs, recencyWindowHours),
        [articleStubs, recencyWindowHours],
    );
    // Popover consumes the detail bodies once loaded; before that we fall
    // back to the metadata stubs so mark-read (which only needs article_ids
    // + published_at) can fire immediately on open without waiting for the
    // detail fetch to resolve.
    const popoverArticles = useMemo(() => (
        Array.isArray(securityArticles) && securityArticles.length > 0
            ? securityArticles
            : articleStubs
    ), [securityArticles, articleStubs]);

    const readSet = readArticleIds instanceof Set ? readArticleIds : null;
    const hasRecent = recentArticles.length > 0;
    const allRecentRead = hasRecent && readSet !== null && recentArticles.every(
        (article) => readSet.has(String(article?.article_id || '')),
    );

    if (!item) {
        return null;
    }

    const articleCount = articleStubs.length;
    const hasNews = articleCount > 0;

    // Article count is still surfaced via the button tooltip / aria-label so
    // it stays discoverable — just no longer a visible badge.
    const buttonTooltip = hasNews
        ? `${articleCount} recent headline${articleCount === 1 ? '' : 's'}`
        : newsFetchMessage(item);

    const rawPct = item?.return_pct;
    const pctNumber = Number(rawPct);
    const hasPct = rawPct !== null && rawPct !== undefined && Number.isFinite(pctNumber);
    const threshold = Number.isFinite(Number(moverThresholdPct))
        ? Number(moverThresholdPct)
        : 2.0;

    const recencyTooltip = formatRecencyDotTooltip(recencyWindowHours, {
        allRead: allRecentRead,
    });
    // Dot state: bright (unread), dull (all read), or hidden.
    const dotVariant = !hasRecent
        ? 'hidden'
        : allRecentRead ? 'dull' : 'bright';

    let buttonTone = hasNews ? 'news' : 'none';
    if (item?.fetch_status === 'pending') buttonTone = 'pending';

    return (
        <>
            <Tooltip content={buttonTooltip}>
                <IconButton
                    label={hasNews
                        ? `Show ${articleCount} recent headline${articleCount === 1 ? '' : 's'}`
                        : buttonTooltip}
                    onClick={handleOpen}
                    aria-busy={item?.fetch_status === 'pending'}
                    data-testid="news-cell-button"
                    // Give the two badge overlays room to overhang the button
                    // bounds, or the pill/dot get clipped.
                    className={cx(BUTTON_CLASS_NAME, BUTTON_TONE_CLASS_NAMES[buttonTone])}
                >
                    <Newspaper
                        aria-hidden="true"
                        data-testid="news-cell-icon"
                        size={NEWS_ICON_FONT_SIZE}
                    />
                    {/* Move pill / em-dash placeholder — top-right badge overlay. */}
                    {hasPct ? (
                        <span
                            data-testid="news-move-pill-slot"
                            className="z-[2] leading-[0]"
                            style={PILL_SLOT_STYLE}
                        >
                            <MovePill
                                pct={pctNumber}
                                threshold={threshold}
                                testId="news-move-pill"
                                density="compact"
                            />
                        </span>
                    ) : (
                        <span
                            data-testid="news-move-number-placeholder"
                            className={PLACEHOLDER_CLASS_NAME}
                            style={PLACEHOLDER_STYLE}
                        >
                            —
                        </span>
                    )}
                    {/* Recency dot — bottom-right badge overlay. Three-state per
                        W8: bright when at least one recent article is unread,
                        dull when the recent set is fully read, hidden when
                        nothing is inside the window. */}
                    {dotVariant !== 'hidden' && (
                        <Tooltip content={recencyTooltip}>
                            <span
                                aria-label={recencyTooltip}
                                data-testid="news-recency-dot"
                                data-variant={dotVariant}
                                className={cx(DOT_CLASS_NAME, DOT_VARIANT_CLASS_NAMES[dotVariant])}
                                style={DOT_STYLE}
                            />
                        </Tooltip>
                    )}
                </IconButton>
            </Tooltip>
            <NewsPopover
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                item={item}
                articles={popoverArticles}
                articlesLoading={securityArticlesLoading}
                articlesError={securityArticlesError}
                moverThresholdPct={moverThresholdPct}
                lastUpdatedAt={item.fetch_status
                    ? (item.news_checked_at ? new Date(item.news_checked_at) : null)
                    : lastUpdatedAt}
                deepDiveEnabled={deepDiveEnabled}
                recencyWindowHours={recencyWindowHours}
                readArticleIds={readSet}
                onArticlesRead={onArticlesRead}
                onDeepDiveRequest={handleDeepDiveRequest}
            />
            <LlmSettingsDialog
                open={settingsDialogOpen}
                onClose={handleSettingsClose}
                onSaved={handleSettingsSaved}
                reason={settingsReason}
            />
            <DeepDiveDialog
                open={deepDiveOpen}
                onClose={handleDeepDiveClose}
                item={item}
                moverThresholdPct={moverThresholdPct}
                credentials={deepDiveCredentials}
                focusAreas={deepDiveFocusAreas}
                onRequireSettings={handleDeepDiveRequireSettings}
            />
        </>
    );
}

NewsCell.propTypes = {
    item: PropTypes.shape({
        cognive_id: PropTypes.string,
        display_name: PropTypes.string,
        return_pct: PropTypes.number,
        is_big_mover: PropTypes.bool,
        recent_articles: PropTypes.array,
        fetch_status: PropTypes.string,
    }),
    moverThresholdPct: PropTypes.number,
    lastUpdatedAt: PropTypes.instanceOf(Date),
    deepDiveEnabled: PropTypes.bool,
    recencyWindowHours: PropTypes.number,
    readArticleIds: PropTypes.instanceOf(Set),
    onArticlesRead: PropTypes.func,
    onLoadNewsDetails: PropTypes.func,
    securityArticles: PropTypes.array,
    securityArticlesLoading: PropTypes.bool,
    securityArticlesError: PropTypes.oneOfType([
        PropTypes.instanceOf(Error),
        PropTypes.string,
        PropTypes.object,
    ]),
};
